import { api } from './api'
import { User, UserFormData } from '../types/user'

export const userService = {
  /**
   * Get all users
   */
  getAll: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/users')
    return response.data
  },

  /**
   * Get clinical staff only
   */
  getClinicalStaff: async (): Promise<User[]> => {
    const response = await api.get<User[]>('/users')
    return response.data.filter((user) => user.roleCategory === 'Clinical' && user.isActive)
  },

  /**
   * Create a new user
   */
  create: async (data: UserFormData): Promise<User> => {
    const response = await api.post<User>('/users', data)
    return response.data
  },

  /**
   * Update a user
   */
  update: async (id: string, data: Partial<UserFormData>): Promise<User> => {
    const response = await api.put<User>(`/users/${id}`, data)
    return response.data
  },

  /**
   * Delete a user
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`)
  },

  /**
   * Activate a user
   */
  activate: async (id: string): Promise<User> => {
    const response = await api.post<User>(`/users/${id}/activate`, {})
    return response.data
  },

  /**
   * Deactivate a user
   */
  deactivate: async (id: string): Promise<User> => {
    const response = await api.post<User>(`/users/${id}/deactivate`, {})
    return response.data
  },

  /**
   * Reset user password
   */
  resetPassword: async (id: string, password: string): Promise<void> => {
    await api.post(`/users/${id}/reset-password`, { password })
  },

  /**
   * Record last login time for the user (called after successful login, fire-and-forget).
   */
  recordLastLogin: async (id: string): Promise<void> => {
    await api.post(`/users/${id}/record-login`)
  },

  /**
   * Upload signature image for a user using presigned POST URL (bypasses API Gateway body parsing)
   */
  uploadSignature: async (id: string, file: File): Promise<void> => {
    // Step 1: Initialize upload and get presigned POST URL
    const initResponse = await api.post<{
      s3Key: string
      uploadUrl: string
      fields: Record<string, string>
    }>(`/users/${id}/signature/init`, {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || 'image/png',
    })

    const { s3Key, uploadUrl, fields } = initResponse.data

    // Step 2: Upload file directly to S3 using presigned POST URL
    const isMockMode = uploadUrl.includes('mock') || uploadUrl.includes('example.com')
    
    if (!isMockMode) {
      const s3FormData = new FormData()
      // Add all fields from presigned POST (must be added before file)
      Object.entries(fields).forEach(([key, value]) => {
        s3FormData.append(key, value)
      })
      // Add the file last (required by S3 presigned POST)
      s3FormData.append('file', file)

      // Upload to S3 using presigned POST URL
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: s3FormData,
      })

      if (!response.ok) {
        throw new Error(`Failed to upload signature to S3: ${response.statusText}`)
      }
    }

    // Step 3: Complete upload and store S3 key in Cognito
    await api.post(`/users/${id}/signature/complete`, { s3Key })
  },

  /**
   * Get presigned URL for user signature
   */
  getSignatureUrl: async (id: string): Promise<string> => {
    const response = await api.get<{ url: string; s3Key?: string }>(`/users/${id}/signature`)
    // Handle both parsed object and string response
    if (typeof response.data === 'string') {
      const parsed = JSON.parse(response.data)
      return parsed.url
    }
    return response.data.url
  },

  /**
   * Delete user signature
   */
  deleteSignature: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}/signature`)
  },
}
